/* eslint-disable no-unused-vars, @typescript-eslint/no-unused-vars */
import { Box, SvgIcon, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CollapsibleTableRow,
  CollapsibleTableData,
} from '@Components/CollapsibleTable';
import Icons from '@Constants/Icons.constants';
import { CallLogTable } from '@Models/report.model';

/** Returns data for table row in desired format. */
export const createData = (
  row: CallLogTable,
  isExpanded: boolean,
  onExpandClick: () => void
): CollapsibleTableData => {
  const {
    address,
    contactName,
    contactEmail,
    phoneNumber,
    status,
    calledAt,
    callDuration,
    connectionDuration,
    totalDuration,
    disposition,
    note,
    transcriptionSummary,
  } = row;

  const renderTranscriptionSummary = () => {
    if (!transcriptionSummary) return '-';

    const SYMBOLS_LIMIT = 100;
    const isLong = transcriptionSummary.length > SYMBOLS_LIMIT;

    return (
      <Typography
        variant="main"
        sx={{ cursor: isLong ? 'pointer' : 'default' }}
        onClick={isLong ? onExpandClick : undefined}
      >
        {isLong && !isExpanded
          ? `${transcriptionSummary.slice(0, SYMBOLS_LIMIT)}...`
          : transcriptionSummary}
      </Typography>
    );
  };

  return {
    primaryData: {
      address,
      contactName: (
        <Box>
          <Typography variant="main">{contactName}</Typography>
          <Typography variant="main" sx={{ color: 'text.secondary' }}>
            {contactEmail}
          </Typography>
        </Box>
      ),
      // TODO: HANDLE ICON FOR PHONE NUMBER ACCORDING TO STATUS
      phone: (
        <Box display="flex" alignItems="center">
          <SvgIcon sx={{ mr: 0.4 }}>{Icons.call}</SvgIcon>
          <Typography variant="main">{phoneNumber}</Typography>
        </Box>
      ),
      status: status || '-',
      disposition: disposition || '-',
      callTiming:
        calledAt || callDuration || connectionDuration || totalDuration ? (
          <Box textAlign="right">
            <Typography variant="main">{calledAt}</Typography>
            <Typography variant="main" sx={{ color: 'text.secondary' }}>
              {`Call Length: ${callDuration || '-'}`}
            </Typography>
            <Typography variant="main" sx={{ color: 'text.secondary' }}>
              {`Time To Connect: ${connectionDuration || '-'}`}
            </Typography>
            <Typography variant="main" sx={{ color: 'text.secondary' }}>
              {`Total Duration: ${totalDuration || '-'}`}
            </Typography>
          </Box>
        ) : (
          '-'
        ),
      note: note || '-',
      transcriptionSummary: renderTranscriptionSummary(),
    },
  };
};

/** Returns Map of Grouped Data according to passed key.
 * @param table - table data
 * @param key - key according to which group is to be created
 */
export const getGroupedRows = (
  table: CallLogTable[],
  key: keyof CallLogTable
): Map<string, CallLogTable[]> =>
  table.reduce(
    (entryMap: Map<string, CallLogTable[]>, e) =>
      entryMap.set(e[key], [...(entryMap.get(e[key]) || []), e]),
    new Map()
  );

/** Returns Rows for Collapsible Table (Call Log) */
export const GetRows = (
  table: CallLogTable[],
  isAdmin?: boolean
): CollapsibleTableRow[] => {
  const { t } = useTranslation();
  const rows: CollapsibleTableRow[] = [];
  const groupedMap = getGroupedRows(table, 'sessionId');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const handleExpandClick = (rowId: string) => {
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  groupedMap.forEach((group, key) => {
    let metaData = '';
    if (group[0].callerId !== null) {
      metaData = metaData.concat(
        'CallerId: ',
        group[0].callerId.includes('trust') ? 'TPR' : group[0].callerId,
        '; '
      );
    }
    if (group[0].excludeDncPhones !== null) {
      metaData = metaData.concat(
        'DNC Filtering: ',
        group[0].excludeDncPhones ? 'Enabled' : 'Disabled',
        '; '
      );
    }
    if (group[0].doNotDialDuplicateNumbers !== null) {
      metaData = metaData.concat(
        'Duplicate Status: ',
        group[0].doNotDialDuplicateNumbers ? 'Enabled' : 'Disabled',
        ';'
      );
    }

    const getRowId = (row: CallLogTable) => `${row.sessionId}|${row.calledAt}`;

    rows.push({
      id: key,
      name: isAdmin ? (
        <Typography variant="main" fontWeight="bold">
          {`${t('reports.Session ID')} ${key} `}
          <Typography
            component="span"
            variant="main"
            fontWeight="bold"
            sx={{ color: 'text.secondary' }}
          >{`(${group[0].accountName})`}</Typography>
          {metaData && (
            <Typography
              component="span"
              variant="main"
              fontWeight="bold"
              sx={{ color: 'text.secondary' }}
            >{` | ${metaData}`}</Typography>
          )}
        </Typography>
      ) : (
        <Typography variant="main" fontWeight="bold">
          {`${t('reports.Session ID')} ${key} `}
          {metaData && (
            <Typography
              component="span"
              variant="main"
              fontWeight="bold"
              sx={{ color: 'text.secondary' }}
            >{`(${metaData})`}</Typography>
          )}
        </Typography>
      ),
      align: {
        callTiming: 'right',
      },
      data: group.map((row: CallLogTable) =>
        createData(row, expandedRows[getRowId(row)], () =>
          handleExpandClick(getRowId(row))
        )
      ),
    });
  });

  return rows;
};
